import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useAutoCheckOffTask } from 'src/app/pages/starterDashboardV2/shared/hooks';
import { useShortUrl } from 'src/app/shared/hooks/shortUrls';
import { shareFacebook } from 'src/app/shared/share/facebook';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { useShareUrlWithUtmParams } from '../../utils/getShareUrlWithUtmParams';

import type { ActionsReturnType, PostSharingHookProps } from './types';

export function useFacebookPostSharing(props: PostSharingHookProps): ActionsReturnType {
	const i18n = useI18n();
	const autoCheckOffTask = useAutoCheckOffTask();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const {
		message,
		trackingProps: { context, viewContext },
		shareUrl = '',
	} = props;

	const shareUrlWithUtmParams = useShareUrlWithUtmParams(shareUrl, 'facebook_post');

	const options = useMemo(
		() => ({
			trackingData: {
				context,
				location: 'facebook_post',
				tab_at_share: viewContext,
				app_available: false,
				content: 'post',
			},
			trackingEvents: {
				intent: 'share_petition_intent',
				success: 'share_petition',
				failure: 'share_petition_failure',
				cancel: 'share_petition_cancel',
			},
		}),
		[context, viewContext],
	);

	// if original shareUrl is empty, shortUrl will also be empty
	const shortUrl = useShortUrl(shareUrlWithUtmParams);

	const primaryAction = useCallback(async () => {
		void copyToClipboard(message, i18n);

		// FIXME? currently calling shareFacebook directly instead of using useAvailableShareActions
		// because useAvailableShareActions is not providing a action() function anymore
		// => would require a rewrite of the "executors" implementation to handle <ShareAction> logic
		await shareFacebook(shortUrl, options, utilityContext, track);

		await autoCheckOffTask('share_facebook');
	}, [i18n, message, autoCheckOffTask, shortUrl, options, track, utilityContext]);

	return {
		primaryAction,
		secondaryAction: undefined,
	};
}
